import { useNavigate } from 'react-router';

type Mode = 'signin' | 'signup' | 'forgot';
type Role = 'customer' | 'professional';

type Props = {
  /** Which auth flow these tabs switch within. */
  mode: Mode;
  /** The role of the page currently being shown. */
  active: Role;
};

/**
 * Customer / Professional switcher shown at the top of the sign-in,
 * sign-up and forgot-password pages. Replaces the old role-chooser
 * screen: instead of picking a role on a separate page first, the
 * visitor lands straight on a form and flips between the two roles
 * here. Each tab routes to the role-specific page for the current mode.
 */
export function AuthTabs({ mode, active }: Props) {
  const navigate = useNavigate();

  const pathFor = (role: Role): string => {
    if (mode === 'signin') {
      return role === 'customer' ? '/signin/customer' : '/professional/signin';
    }
    if (mode === 'forgot') {
      return role === 'customer'
        ? '/forgot-password'
        : '/professional/forgot-password';
    }
    return role === 'customer' ? '/signup/customer' : '/professional/signup';
  };

  const go = (role: Role) => {
    if (role !== active) navigate(pathFor(role));
  };

  return (
    <div className="w-full max-w-[420px] mx-auto mb-10">
      <div className="grid grid-cols-2 gap-1.5 bg-[#f4f4f4] rounded-[14px] p-1.5">
        <Tab
          label="Customer"
          icon="spa"
          isActive={active === 'customer'}
          onClick={() => go('customer')}
        />
        <Tab
          label="Professional"
          icon="work"
          isActive={active === 'professional'}
          onClick={() => go('professional')}
        />
      </div>
    </div>
  );
}

function Tab({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={`flex items-center justify-center gap-2 h-[44px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[14px] tracking-[-0.1px] transition-all ${
        isActive
          ? 'bg-white text-[#8e2a2f] shadow-[0px_2px_6px_0px_rgba(153,155,168,0.2)]'
          : 'bg-transparent text-[#908e8e] hover:text-[#3a3335]'
      }`}
    >
      <span className="material-icons text-[18px]">{icon}</span>
      {label}
    </button>
  );
}
